import React, { useState, useEffect, useRef } from 'react';

const ServiceTerminationNotice = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [mode, setMode] = useState(null); // 'popup' or 'banner'

    useEffect(() => {
        const checkVisibility = async () => {
            // 1. Check Country
            let countryCode = getCookie('user_country');

            // If cookie missing, try to fetch it
            if (!countryCode) {
                try {
                    const response = await fetch('https://ipwho.is/');
                    const data = await response.json();
                    if (data && data.country_code) {
                        countryCode = data.country_code;
                        // Set cookie to avoid re-fetching (matching logic in CopyrightSec)
                        let domainAttr = "";
                        if (window.location.hostname.includes('rebless.clinic')) {
                            domainAttr = "; Domain=.rebless.clinic";
                        }
                        document.cookie = `user_country=${countryCode}; Path=/; Secure; SameSite=Lax; Max-Age=86400${domainAttr}`;
                    }
                } catch (e) {
                    console.error("Failed to fetch country", e);
                    return;
                }
            }

            // Only show if strictly NOT 'KR'
            // If fetching failed and countryCode is still null, we default to NOT showing to be safe, 
            // or we could show it? Usually safe to NOT show if unknown.
            // But user requirement is "If access region is not Korea".
            // If we can't determine, maybe assume Korea? Or assume Global?
            // "rebless.clinic" suggests global context. 
            // Let's assume if we can't detect, we don't annoy the user.
            if (!countryCode || countryCode === 'KR') {
                return;
            }

            // 2. Check "Seen" status
            const hasSeen = localStorage.getItem('service_closure_notice_seen');

            if (hasSeen) {
                setMode('banner');
            } else {
                setMode('popup');
            }
            setIsVisible(true);
        };

        checkVisibility();
    }, []);

    const bannerRef = useRef(null);

    // Adjust body margin when banner is visible
    useEffect(() => {
        if (mode === 'banner' && isVisible) {
            const updateMargin = () => {
                if (bannerRef.current) {
                    const height = bannerRef.current.offsetHeight;
                    document.body.style.marginTop = `${height}px`;
                    // Also try to push header if it has a known class? 
                    // But body margin usually suffices if Header is fixed relative to body??
                    // No, fixed elements are relative to viewport. Body margin affects flow content.
                    // If Header is fixed to viewport top, body margin does NOTHING to it.
                    // We need to push the Header element itself if we can select it.
                    const header = document.querySelector('nav.header');
                    if (header) {
                        header.style.top = `${height}px`;
                    }
                }
            };

            // Run initially and on resize
            updateMargin();
            window.addEventListener('resize', updateMargin);
            return () => {
                document.body.style.marginTop = '';
                const header = document.querySelector('nav.header');
                if (header) {
                    header.style.top = '';
                }
                window.removeEventListener('resize', updateMargin);
            };
        }
    }, [mode, isVisible]);

    const getCookie = (name) => {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(';').shift();
    };

    const handleClosePopup = () => {
        localStorage.setItem('service_closure_notice_seen', 'true');
        setIsVisible(false);
        // After closing popup, we don't show banner immediately. 
        // It will show on next reload as 'banner'.
    };

    // Prevent scrolling when popup is open
    useEffect(() => {
        if (mode === 'popup' && isVisible) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [mode, isVisible]);


    if (!isVisible || !mode) return null;

    const Content = () => (
        <>
            <h2 style={styles.title}>Notice of Service Closure</h2>
            <p style={styles.text}>Thank you for using <strong>rebless.clinic</strong>.</p>
            <p style={styles.text}>
                Please note that the platform will be discontinued on <strong>February 28, 2026</strong>,
                as it was launched as a proof-of-concept (POC) service.
            </p>
            <p style={styles.text}>
                Access will remain available until the closure date.
                We recommend backing up any necessary information before the service ends.
            </p>
            <p style={styles.text}>
                For inquiries or support, please contact <a href="mailto:contact@hroboticsinc.com" style={styles.link}>contact@hroboticsinc.com</a>.
            </p>
            <p style={styles.lastText}>We sincerely appreciate your support and understanding.</p>
        </>
    );

    if (mode === 'popup') {
        return (
            <div style={styles.overlay}>
                <div style={styles.popup}>
                    <button onClick={handleClosePopup} style={styles.closeButton}>&times;</button>
                    <div style={styles.popupContent}>
                        <Content />
                    </div>
                    <div style={styles.popupFooter}>
                        <button onClick={handleClosePopup} style={styles.confirmButton}>Close</button>
                    </div>
                </div>
            </div>
        );
    }

    // Banner Mode
    return (
        <div style={styles.banner} ref={bannerRef}>
            <div style={styles.bannerContainer}>
                <div style={styles.bannerContent}>
                    <span style={styles.bannerTitle}>Notice of Service Closure: </span>
                    <span style={styles.bannerText}>
                        Our service will be discontinued on Feb 28, 2026. Please backup your data.
                        Contact: <a href="mailto:contact@hroboticsinc.com" style={styles.bannerLink}>contact@hroboticsinc.com</a>
                    </span>
                </div>
            </div>
        </div>
    );
};

const styles = {
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(5px)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
    },
    popup: {
        backgroundColor: '#fff',
        borderRadius: '16px',
        width: '100%',
        maxWidth: '500px',
        boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        animation: 'fadeIn 0.3s ease-out',
        overflow: 'hidden',
    },
    closeButton: {
        position: 'absolute',
        top: '15px',
        right: '20px',
        background: 'none',
        border: 'none',
        fontSize: '28px',
        cursor: 'pointer',
        color: '#666',
        lineHeight: 1,
        zIndex: 1,
    },
    popupContent: {
        padding: '40px 30px 20px',
        textAlign: 'left',
    },
    popupFooter: {
        padding: '0 30px 30px',
        textAlign: 'right',
    },
    title: {
        fontSize: '24px',
        fontWeight: '700',
        marginBottom: '20px',
        color: '#1a1a1a',
        borderBottom: '2px solid #007bff',
        paddingBottom: '10px',
        display: 'inline-block',
    },
    text: {
        fontSize: '16px',
        lineHeight: '1.6',
        color: '#444',
        marginBottom: '15px',
    },
    lastText: {
        fontSize: '16px',
        fontWeight: '600',
        color: '#1a1a1a',
        marginTop: '20px',
    },
    link: {
        color: '#007bff',
        textDecoration: 'none',
        fontWeight: '500',
    },
    confirmButton: {
        backgroundColor: '#007bff',
        color: 'white',
        border: 'none',
        padding: '12px 24px',
        borderRadius: '8px',
        fontSize: '16px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'background-color 0.2s',
    },
    // Banner Styles
    banner: {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        backgroundColor: '#2c3e50',
        color: '#fff',
        zIndex: 100000,
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        display: 'flex',
        justifyContent: 'center',
    },
    bannerContainer: {
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '12px 20px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
    },
    bannerContent: {
        fontSize: '14px',
        lineHeight: '1.5',
    },
    bannerTitle: {
        fontWeight: '700',
        color: '#ff6b6b', // Accent color
        marginRight: '8px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
    bannerText: {
        color: '#ecf0f1',
    },
    bannerLink: {
        color: '#3498db',
        textDecoration: 'underline',
        marginLeft: '5px',
    },
};

export default ServiceTerminationNotice;
